import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AIJobStatus, BatchCampaignPreparation } from 'types'
import { useMutation, useQueryClient } from 'react-query'
import {
  Props,
  Variables as CreatePreparationVariables,
  useCreateBatchCampaignPreparation,
  Data,
} from './useCreateBatchCampaignPreparation'
import { UseMutationOptions } from 'react-query/types/react/types'

export interface FetchPreparationProps {
  id: number
  workspaceId: number
  brandPlanId: number
  signal?: AbortSignal
}


export const fetchPreparation = async ({ id, workspaceId, brandPlanId }: FetchPreparationProps) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plans/${brandPlanId}/batch_campaign_preparations/${id}`)
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export type Options = Omit<
  UseMutationOptions<BatchCampaignPreparation, Error, CreatePreparationVariables>,
  'mutationFn'
>

export const usePrepareBatchCampaigns = (props: Omit<Props, 'workspaceId'>, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: createPreparation } = useCreateBatchCampaignPreparation(props)

  const queryClient = useQueryClient()
  return useMutation<BatchCampaignPreparation, Error, CreatePreparationVariables>(async (vars) => {
    const { signal } = vars

    let { batch_campaign_preparation: batchCampaignPreparation } = await createPreparation(vars)

    const interval = 500

    do {
      const result = await queryClient.fetchQuery(
        [
          'workspaces',
          workspaceId,
          'brand_plans',
          'detail',
          'batch_campaign_preparations',
          'detail',
          batchCampaignPreparation.id,
        ],
        () => fetchPreparation({ workspaceId, id: batchCampaignPreparation.id, ...props }),
      )

      batchCampaignPreparation = result.batch_campaign_preparation

      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (batchCampaignPreparation?.status === AIJobStatus.Failed) {
        throw new Error('Failed to prepare batch campaigns.')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (
      ![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(batchCampaignPreparation?.status)
    )

    return batchCampaignPreparation
  }, options)
}
